import { useQuery } from '@tanstack/react-query';
import { USD_DECIMALS } from '@ui/constants/numbersDecimals';
import { Interval } from '@ui/constants/poll';
import { useDataChainId } from '@ui/hooks/datachain';
import { toReadableValue } from '@ui/lib/bignumber';
import { getAnyCollateralConfigFacet } from '@ui/services/contract/contract-instances/getAnyCollateralConfigFacet';
import { getVaultFacet } from '@ui/services/contract/contract-instances/getVaultFacet';
import { PayablePoolToken } from '@ui/types/token';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { usePublicClient } from 'wagmi';

const getDirectCollateralErrorReason = (error: unknown) => {
  const message =
    error instanceof Error
      ? extractErrorMessage(error.toString()) ?? error.message
      : undefined;

  if (!message) return 'Collateral unavailable';

  if (message.includes('collateral oracle price too old')) {
    return 'Price updating';
  }

  if (message.includes('oracle disabled')) {
    return 'Collateral unavailable';
  }

  return message;
};

export const useDirectCollateralOpenStatus = (
  token: PayablePoolToken | undefined,
) => {
  const chainId = useDataChainId();
  const client = usePublicClient({ chainId });
  const isDirectCollateral = token?.isDirectCollateral === true;
  const tokenAddress = token?.address;

  const query = useQuery({
    refetchInterval: Interval.FAST,
    queryKey: [
      'AnyCollateral.openStatus',
      chainId,
      client,
      tokenAddress,
    ],
    queryFn: async () => {
      if (isNil(client) || isNil(token) || isNil(tokenAddress)) {
        return undefined;
      }

      const anyCollateralConfigFacet = getAnyCollateralConfigFacet({
        chainId,
        publicClient: client,
      });
      const vaultFacet = getVaultFacet({
        chainId,
        publicClient: client,
      });

      const [
        directCollateralEnabled,
        collateralTokenEnabled,
        collateralOracleConfig,
        collateralOpenCap,
        marginToken,
      ] = await Promise.all([
        anyCollateralConfigFacet.read.isDirectCollateralEnabled(),
        anyCollateralConfigFacet.read.isCollateralTokenEnabled([tokenAddress]),
        anyCollateralConfigFacet.read.getCollateralOracleConfig([tokenAddress]),
        anyCollateralConfigFacet.read.getCollateralOpenCap([tokenAddress]),
        vaultFacet.read.getTokenForTrading([tokenAddress]),
      ]);
      const maxOpenAmount = toReadableValue(
        collateralOpenCap.maxOpenAmount,
        token.decimals,
      );
      const usedOpenAmount = toReadableValue(
        collateralOpenCap.usedOpenAmount,
        token.decimals,
      );
      const remainingOpenAmount = maxOpenAmount.isZero()
        ? undefined
        : BigNumber.maximum(maxOpenAmount.minus(usedOpenAmount), 0);

      return {
        directCollateralEnabled,
        collateralTokenEnabled,
        collateralOracleEnabled: collateralOracleConfig.enabled,
        openCap: {
          maxOpenAmount,
          usedOpenAmount,
          remainingOpenAmount,
          isUnlimited: maxOpenAmount.isZero(),
        },
        marginToken,
        price: toReadableValue(marginToken.price, USD_DECIMALS),
      };
    },
    enabled: isDirectCollateral && !!client && !!tokenAddress,
  });

  const reason = useMemo(() => {
    if (!isDirectCollateral) return undefined;
    if (query.isLoading) return 'Price updating';
    if (query.error) return getDirectCollateralErrorReason(query.error);
    if (!query.data) return 'Collateral unavailable';
    if (!query.data.directCollateralEnabled) return 'Direct collateral disabled';
    if (!query.data.collateralTokenEnabled) return 'Collateral token disabled';
    if (!query.data.collateralOracleEnabled) return 'Collateral oracle disabled';
    if (!query.data.marginToken.switchOn) return 'Margin token disabled';

    return undefined;
  }, [
    isDirectCollateral,
    query.data,
    query.error,
    query.isLoading,
  ]);

  return {
    ...query,
    isDirectCollateral,
    isReady: isDirectCollateral ? !reason : true,
    reason,
    price: isDirectCollateral ? query.data?.price : undefined,
  };
};
